import { Component, ViewEncapsulation, ViewChild, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { fadeInOut } from '../../shared/helpers/animations';
import { AuthorizationService, OrderService, SettingsService, StorageManagerService } from '../../shared/services/index';
import { LCRFormService } from '../../shared/services/lcr-form.service';
import { GridComponent } from '@syncfusion/ej2-angular-grids';
import { NgbTabset } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { DomSanitizer } from '@angular/platform-browser';
import { Subscription, interval } from 'rxjs';
import { TooltipComponent } from '@syncfusion/ej2-angular-popups';
import { DeleteButtonComponent } from '../common/delete-button/delete-button.component';
import { ClickEventArgs } from '@syncfusion/ej2-angular-navigations/src';
import { Utilities } from '../../shared/services/utilities';
import { EsigModalComponent } from '../common/esig-modal/esig-modal.component';
import { PragmaticValidationService } from '../../shared/services/pragmatic-validation.service';
import { RTCDPragmaticValidation } from '../../shared/models/rtcd_pragmatic_validation.model';
import { FormType } from '../../shared/models/form-type';

@Component({
  selector: 'app-lcr-form-dashboard',
  templateUrl: './lcr-form-dashboard.component.html',
  styleUrls: ['./lcr-form-dashboard.component.scss'],
  animations: [fadeInOut],
  encapsulation: ViewEncapsulation.Emulated
})
export class LCRFormDashboardComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('LCRForm_grid')
  public LCRForm_grid: GridComponent;
  @ViewChild('drafts_grid')
  public draftsGrid: GridComponent;
  @ViewChild('deliveries_grid')
  public deliverieGrid: GridComponent;
  @ViewChild('tabset', { static: true })
  public tabset: NgbTabset;
  @ViewChild('tooltip')
  public tooltip: TooltipComponent;
  @ViewChild('deleteLCRButton')
  public deleteButton: DeleteButtonComponent;
  @ViewChild('deleteLCRDraftButton')
  public deleteButtonDrafts: DeleteButtonComponent;
  @ViewChild('deleteLCRDeliveryButton')
  public deleteButtonDelivery: DeleteButtonComponent;
  @ViewChild('sendToFDAButton')
  public sendToFDAButton: EsigModalComponent;
  selectedLCRRevisionDraftMode: boolean | undefined;
  selectedLCRRevisionProductType: string | undefined;
  selectedLCRRevisionFileName: string | undefined;
  selectedLCRRevisionVersion: string | undefined;

  LCRForm_grid_data: any[];
  LCRDraftForm_grid_data: any[];
  LCRDeliveries_grid_data: any[];
  public initialPage: Object;
  public formatOptions: object;
  public toolbarOptions: Object;
  count_LCRForm: number;
  count_draftLCRForm: number;
  count_deliveriesLCRForm: number;
  loading = false;
  submitted = false;
  public searchForm: UntypedFormGroup;
  errorMessage: string;
  bHasPermission = false;
  sub: Subscription;
  formData: any;
  activeTab: string;
  public clipmodecolumn = { class: 'e-tooltipcolumn' };
  hasDeletePermission = false;
  selectedLcrRevisionId: number | undefined;

  needsToPoll: boolean = true;

  constructor (private router: Router, private orderService: OrderService, private toastr: ToastrService, private storageManagerService: StorageManagerService,
    private fb: UntypedFormBuilder, private authorizationService: AuthorizationService, private activatedRoute: ActivatedRoute, private sanitizer: DomSanitizer,
    private pragmaticValidationService: PragmaticValidationService,
    private settingsService: SettingsService,
    private lcrFormService: LCRFormService,
    ) {
  }

  ngAfterViewInit(): void {}

  ngOnInit(): void {

    this.pollData();

    this.searchForm = this.fb.group({
      searchText: ['', { validators: [Validators.required], updateOn: 'blur' }]
    });

    // Check if user has write permission. If no, disable the "Save", "Save a draft" buttons
    this.checkUserPermission();

    this.toolbarOptions = this.createCustomToolbarItems(this.hasDeletePermission, this.bHasPermission);

    this.initialPage = { currentPage: 1, pageSize: 25, pageSizes: [25, 50, 75, 100, 'All'] };
    this.formatOptions = { type: 'dateTime', format: Utilities.getLocaleDateString() + ' hh:mm a' };

    this.activeTab = 'LCRFormGrid';
    // set focus on the invisible div so the first tab will bring it to the "Skip to Main Navigation" button because it must be the first tab target on the page (508 Compliance)
    this.initTabIndex();
  }

  pollData() {
    this.getDeliveryDashboard(true); // Start polling immeditely
    // Poll every minute (60000 milliseconds) only if needsToPoll = true
    this.sub = interval(60000).subscribe(() => {
      if (this.needsToPoll) {
        this.getDeliveryDashboard(false);
      }
    });
  }

  getDeliveryDashboard(isInitialCall) {
    if (isInitialCall) {
      this.formData = this.activatedRoute.snapshot.data['formData'];
      this.gridDataSetup();
    } else {
      this.lcrFormService.getLCRDelilveries().subscribe({
        next: (result: any) => {
          if(result && result.data.length) {
            this.processDeliveryDashboardData(result);
            this.gridDataSetup();
          }
          else{
            this.loading = false
          }

        }
      })
    }
  }

  processDeliveryDashboardData(result: any) {
    const today: Date = new Date();
    const numberOfDaysAgo: Date = new Date(today.getTime() - parseInt(this.settingsService.pragmaticValidatorAutoRefreshDays, 10) * 24 * 60 * 60 * 1000);

    this.LCRDeliveries_grid_data = result.data.map(x => {
      if (x.pragmaticValidationDate) {
        x.pragmaticValidationDate = new Date(x.pragmaticValidationDate);
      }
      return x;
    });
    
    const filteredDataPragmaticTaskStatusInProgress = this.LCRDeliveries_grid_data.filter(item => item.pragmaticTaskStatusCode === 1);
    this.loading = false;
    const filteredDataWithConfigDaysAgo = filteredDataPragmaticTaskStatusInProgress.some(item => {
      const createDate: Date = new Date(item.pragmaticValidationDate);
      return createDate >= numberOfDaysAgo;
    });

    if (!filteredDataWithConfigDaysAgo) {
      this.needsToPoll = false;
    }
    this.formData[1].data = this.LCRDeliveries_grid_data;
  }

  gridDataSetup() {
    // LCR form list
    if (this.formData) {
      if (this.formData.error) {
          this.toastr.error(this.formData.error);
          this.formData = null;
      } else if (!Array.isArray(this.formData)) {
          this.toastr.error(this.formData);
          this.formData = null;
      } else {
          if (this.formData[0]) {
              this.LCRForm_grid_data = this.formData[0].data.filter(x => x.isDraftMode === false);
              this.count_LCRForm = this.LCRForm_grid_data.length;
              this.LCRDraftForm_grid_data = this.formData[0].data.filter(x => x.isDraftMode === true);
              this.count_draftLCRForm = this.LCRDraftForm_grid_data.length;
          }

          // Merge Deliveries
          if (this.formData && this.formData[1]) {
              this.LCRDeliveries_grid_data = this.formData[1].data;
              this.count_deliveriesLCRForm = this.LCRDeliveries_grid_data.length;
          }

          if (this.deliverieGrid) {
            this.deliverieGrid.refresh();
          }
      }
    }
  }

  pragmaticValidation() {
    let foundItemIndex = this.LCRDeliveries_grid_data.findIndex(x => x.fileName === this.selectedLCRRevisionFileName);
    if (this.LCRDeliveries_grid_data[foundItemIndex].pragmaticTaskStatusCode === 1) {
      this.toastr.warning(`Pragmatic validation is already in process.`, '', { closeButton: true });
      return; 
    }

    if (foundItemIndex != -1 ) {
      const customerGuid = this.settingsService.getCustomerGuid();
      let pragmaticData: RTCDPragmaticValidation = {
        formRevisionId: this.LCRDeliveries_grid_data[foundItemIndex].formRevisionID,
        sourceFilePath: this.LCRDeliveries_grid_data[foundItemIndex].filePath,
        sourceFileName: this.LCRDeliveries_grid_data[foundItemIndex].fileName,
        fileType: FormType.LCR,
        createUser: this.storageManagerService.read('userData').userguid,
        changeUser: this.storageManagerService.read('userData').userguid,
        customerGuid,
      }
  
      this.pragmaticValidationService.pragmaticValidation(pragmaticData).subscribe({
        next: (result) => {
          this.LCRDeliveries_grid_data[foundItemIndex].pragmaticTaskStatusCode = 1;
          this.LCRDeliveries_grid_data[foundItemIndex].pragmaticTaskStatus = 'InProgress';
          this.deliverieGrid.refresh();
          this.needsToPoll = true;
        },
        error: (error) => {
          this.toastr.error(error && error.errorMessage ? error.errorMessage : error);
        },
      });
    }
  }

  initTabIndex() {
    (document.activeElement as HTMLElement).blur();

    const main = document.getElementById("firstDiv");

    if (main) {
      main.focus();
    }
  }

  tabChanged(event) {
    this.activeTab = event.nextId;
    // Setup the tool bar configuration
    this.lcrFormGridRowDeselected();
    this.toolbarOptions = this.createCustomToolbarItems(this.hasDeletePermission, this.bHasPermission);
    this.selectedLcrRevisionId = undefined;
  }

  getActiveGrid() {
    let grid;

    switch (this.activeTab) {
      case 'drafts':
        grid = this.draftsGrid;
        break;
      case 'deliveries':
        grid = this.deliverieGrid;
        break;
      case 'LCRFormGrid':
        grid = this.LCRForm_grid;
        break;
    }

    return grid;
  }

  tooltipValue(args) {
    if (args.target.scrollWidth <= args.target.clientWidth) {
      args.cancel = true;
    } else {

      const grid = this.getActiveGrid();
      const rowInfo = grid?.getRowInfo(args.target);
      const rowData = rowInfo.rowData;
      this.tooltip.cssClass = 'customTooltip';
      this.tooltip.content = rowData[(rowInfo.column as any).field];
    }
  }

    // Change grid filter operator from "startwith" to "contains" for columns that are string
    public begin(args: any): void {
        if (args.requestType === 'filtering') {
            if (args.columns) {
                args.columns.forEach(col => {
                  switch (args.currentFilteringColumn) {
                    case 'formVersion':
                      args.currentFilterObject.value = `${ args.currentFilterObject.value }.0`;
                      break;
                        case 'formatedModifiedDate':
                            break;
                        default:
                            col.operator = 'contains';       // set the “contains” operator for the column
                            break;
                    }
                });
            }
        }
    }

  // Display detail of a single LCR form
  viewLCRForm(lcrRevisionId: string) {
    if (lcrRevisionId) {
      this.router.navigate(['/lcr-form', lcrRevisionId]);
    }
  }

  // Download spl files
  downloadFile(filePath: string, fileName: string) {

    this.sub = this.orderService.downloadFile(filePath, fileName).subscribe(response => {
      // non-IE
      const URL = window.URL || (<any>window).webkitURL;
      const downloadUrl = URL.createObjectURL(response);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      link.click();
    },
      error => {
        this.toastr.error(error && error.errorMessage ? error.errorMessage : error);
      }
    );
  }

  // Check if user has write permission. If no, disable the "Save", "Save a draft" buttons
  checkUserPermission() {
    if (this.authorizationService.hasPermission('SSDP.CanWriteLCRForm')) {
      this.bHasPermission = true;
    }
    if (this.authorizationService.hasPermission('SSDP.CanDeleteLCRForm')) {
      this.hasDeletePermission = true;
    }
  }

  createCustomToolbarItems(deletePermission : boolean, writePermission: boolean){
    let toolbarItems = [];

    if (this.activeTab === 'deliveries' && writePermission) {
      toolbarItems.push(
        {
          text: 'Send to FDA',
          tooltipText: 'Send to FDA',
          prefixIcon: 'fas fa-paper-plane',
          id: 'tool-bar-send'
        }
      );
    }

    if (writePermission) {
        toolbarItems.push(
            {
                text: 'Create New LCR',
                tooltipText: 'Add a new LCR to this list',
                prefixIcon: 'fas fa-plus',
                id: 'create-form-btn'
            }
        );
    }

    if(deletePermission){
        toolbarItems.push(
            {
                text: 'Delete LCR',
                tooltipText: 'Delete the selected LCR',
                prefixIcon: 'fas fa-trash',
                id: 'tool-bar-delete'
            }
        )
    }

    if (this.activeTab === 'deliveries' && writePermission) {
      toolbarItems.push(
          {
              text: 'Pragmatic',
              tooltipText: 'Validate the file with Pragmatic',
              prefixIcon: 'fal fa-circle-parking',
              id: 'validation-form-btn'
          }
      );
    }

    toolbarItems.push('Search');

    return toolbarItems;
  }

  onSubmitionToFDA(isSuccessfulySubmitted: boolean) {
    if (isSuccessfulySubmitted) {
      if (this.deliverieGrid) {
        this.deliverieGrid.clearSelection();
      }
    }
  }

    // Grid toolbar click event handler
    toolbarClick(args: ClickEventArgs): void {

        if (args.item.id === 'create-form-btn' && this.bHasPermission) {
            this.router.navigate(["/lcr-form"]);
        }
        else if (!this.selectedLcrRevisionId &&
            (args.item.id === 'tool-bar-delete' || args.item.id === 'tool-bar-send' || args.item.id === 'validation-form-btn')) {
          this.toastr.warning(`No Row(s) selected.`, '', { closeButton: true });
          return;
        }
        else if (args.item.id === 'tool-bar-delete' && this.selectedLcrRevisionId) {
            if(this.LCRForm_grid){
                this.deleteButton.revisionId = this.selectedLcrRevisionId.toString();
                this.deleteButton.openDeleteModal();
            }
            else if(this.draftsGrid){
                this.deleteButtonDrafts.revisionId = this.selectedLcrRevisionId.toString();
                this.deleteButtonDrafts.openDeleteModal();
            }
            else if(this.deliverieGrid){
                this.deleteButtonDelivery.revisionId = this.selectedLcrRevisionId.toString();
                this.deleteButtonDelivery.openDeleteModal();
            }
        }
        else if (this.deliverieGrid && this.selectedLCRRevisionFileName && args.item.id === 'tool-bar-send') {
          this.sendToFDAButton.showEsignatureModal();
        } else if (this.deliverieGrid && this.selectedLCRRevisionFileName && args.item.id === 'validation-form-btn') {
          this.pragmaticValidation()
        }
    }

  ngOnDestroy(): void {

    if (this.sub) {
      this.sub.unsubscribe();
    }
  }
  // convenience getter for easy access to form fields
  get f() { return this.searchForm.controls; }

  lcrFormGridRowSelected(e: any) {
    this.selectedLcrRevisionId = e.data.lcrRevisionId;

    this.selectedLCRRevisionProductType = e.data.documentType;
    this.selectedLCRRevisionFileName = e.data.fileName;
    this.selectedLCRRevisionVersion = e.data.formVersion;
  }

  lcrFormGridRowDeselected() {
    this.selectedLcrRevisionId = undefined;

    this.selectedLCRRevisionProductType = undefined;
    this.selectedLCRRevisionFileName = undefined;
    this.selectedLCRRevisionVersion = undefined;
  }

  rowSelecting(e) {
    e.cancel = e.isHeaderCheckboxClicked;
    const currentGrid = this.getActiveGrid();
    if (currentGrid?.getSelectedRecords().length) {
      currentGrid.clearSelection();
    }
  }
}
